const FOUNDATION_SUBJECT_DEFINITIONS = {
    HL: {
        subjectName: 'Home Language',
        mode: 'weighted',
        components: [
            {
                key: 'listeningSpeaking',
                label: 'Listening & Speaking',
                reportScaleMax: 10,
                weight: 20,
                sections: [
                    { label: 'Formal', maxScore: 10 },
                    { label: 'Task', maxScore: 10 }
                ]
            },
            {
                key: 'phonics',
                label: 'Phonics',
                reportScaleMax: 10,
                weight: 20,
                sections: [
                    { label: 'Combined Spelling', maxScore: 20 },
                    { label: 'Formal', maxScore: 10 },
                    { label: 'Task', maxScore: 20 }
                ]
            },
            {
                key: 'readingComprehension',
                label: 'Reading & Comprehension',
                reportScaleMax: 10,
                weight: 25,
                sections: [
                    { label: 'Formal', maxScore: 25 },
                    { label: 'Individual Reading', maxScore: 10 },
                    { label: 'Task', maxScore: 20 }
                ]
            },
            {
                key: 'handwriting',
                label: 'Handwriting',
                reportScaleMax: 5,
                weight: 10,
                sections: [
                    { label: 'Formal', maxScore: 10 },
                    { label: 'Task', maxScore: 10 }
                ]
            },
            {
                key: 'writing',
                label: 'Writing',
                reportScaleMax: 10,
                weight: 25,
                sections: [
                    { label: 'Formal', maxScore: 10 },
                    { label: 'Task', maxScore: 10 }
                ]
            }
        ]
    },
    FAL: {
        subjectName: 'First Additional Language',
        mode: 'weighted',
        components: [
            {
                key: 'listeningSpeaking',
                label: 'Listening & Speaking',
                reportScaleMax: 30,
                weight: 50,
                sections: [{ label: 'Listening & Speaking Total', maxScore: 30 }]
            },
            {
                key: 'phonics',
                label: 'Phonics',
                reportScaleMax: 30,
                weight: 20,
                sections: [{ label: 'Phonics Total', maxScore: 30 }]
            },
            {
                key: 'readingComprehension',
                label: 'Reading & Comprehension',
                reportScaleMax: 30,
                weight: 25,
                sections: [{ label: 'Reading & Comprehension Total', maxScore: 30 }]
            },
            {
                key: 'writing',
                label: 'Writing',
                reportScaleMax: 20,
                weight: 5,
                sections: [{ label: 'Writing Total', maxScore: 20 }]
            }
        ]
    },
    LIFE_SKILLS: {
        subjectName: 'Life Skills',
        mode: 'aggregate',
        components: [
            { key: 'beginningKnowledgePsw', label: 'Beginning Knowledge & PSW', sections: [{ label: 'Beginning Knowledge & PSW', maxScore: 30 }] },
            { key: 'performingArts', label: 'Performing Arts', sections: [{ label: 'Performing Arts', maxScore: 10 }] },
            { key: 'visualArts', label: 'Visual Arts', sections: [{ label: 'Visual Arts', maxScore: 10 }] },
            { key: 'physicalEducation', label: 'Physical Education', sections: [{ label: 'Physical Education', maxScore: 10 }] }
        ]
    },
    MATHEMATICS: {
        subjectName: 'Mathematics',
        mode: 'aggregate',
        components: [
            { key: 'numbersOperations', label: 'Numbers, Operations (NOR)', sections: [{ label: 'Numbers, Operations (NOR)', maxScore: 20 }] },
            { key: 'patternsFunctionsAlgebra', label: 'Patterns, Functions, Algebra', sections: [{ label: 'Patterns, Functions, Algebra', maxScore: 10 }] },
            { key: 'spaceAndShape', label: 'Space and Shape', sections: [{ label: 'Space and Shape', maxScore: 10 }] },
            { key: 'measurement', label: 'Measurement', sections: [{ label: 'Measurement', maxScore: 10 }] },
            { key: 'dataHandling', label: 'Data Handling', sections: [{ label: 'Data Handling', maxScore: 5 }] }
        ]
    }
};

const FOUNDATION_SUBJECTS = [1, 2, 3].reduce((acc, grade) => {
    Object.entries(FOUNDATION_SUBJECT_DEFINITIONS).forEach(([suffix, definition]) => {
        const code = `G${grade}_${suffix}`;
        acc[code] = {
            code,
            name: `Grade ${grade} ${definition.subjectName}`,
            mode: definition.mode,
            components: definition.components
        };
    });
    return acc;
}, {});

const normalizeText = (value) => String(value || '').trim();

const clamp = (value, min, max) => Math.min(Math.max(value, min), max);

export function calculateComponentMark(rawScore, maxScore, targetScale) {
    const safeRaw = Number(rawScore);
    const safeMax = Number(maxScore);
    const safeScale = Number(targetScale);

    if (!Number.isFinite(safeRaw) || !Number.isFinite(safeMax) || !Number.isFinite(safeScale)) {
        throw new Error('calculateComponentMark received a non-numeric value.');
    }
    if (safeMax <= 0 || safeScale <= 0) return 0;

    return Math.round((clamp(safeRaw, 0, safeMax) / safeMax) * safeScale);
}

export function getNationalRatingCode(percentage) {
    const rounded = Math.round(clamp(Number(percentage) || 0, 0, 100));
    if (rounded >= 80) return 7;
    if (rounded >= 70) return 6;
    if (rounded >= 60) return 5;
    if (rounded >= 50) return 4;
    if (rounded >= 40) return 3;
    if (rounded >= 30) return 2;
    return 1;
}

export function inferFoundationSubjectCode(subjectName, grade, gradingMode, foundationTemplateCode) {
    if (gradingMode === 'foundation' && FOUNDATION_SUBJECTS[foundationTemplateCode]) {
        return foundationTemplateCode;
    }

    const numericGrade = parseInt(grade, 10);
    if (![1, 2, 3].includes(numericGrade)) {
        return null;
    }

    const normalized = normalizeText(subjectName).toLowerCase();

    if (
        normalized.includes('first additional language') ||
        normalized.includes('additional language') ||
        normalized.includes('eerste addisionele taal') ||
        /\bfal\b/.test(normalized)
    ) {
        return `G${numericGrade}_FAL`;
    }

    if (
        normalized.includes('home language') ||
        normalized.includes('huistaal') ||
        /\bhl\b/.test(normalized)
    ) {
        return `G${numericGrade}_HL`;
    }

    if (
        normalized.includes('life skills') ||
        normalized.includes('lewensvaardigheid') ||
        normalized.includes('lewensvaardighede')
    ) {
        return `G${numericGrade}_LIFE_SKILLS`;
    }

    if (
        normalized.includes('mathematics') ||
        /\bmaths?\b/.test(normalized) ||
        normalized.includes('wiskunde')
    ) {
        return `G${numericGrade}_MATHEMATICS`;
    }

    return null;
}

export function getFoundationSubjectDefinition(subjectName, grade, gradingMode, foundationTemplateCode) {
    const code = inferFoundationSubjectCode(subjectName, grade, gradingMode, foundationTemplateCode);
    return code ? FOUNDATION_SUBJECTS[code] || null : null;
}

export function calculateSubjectMark(subjectCode, components, options = {}) {
    const definition = FOUNDATION_SUBJECTS[subjectCode];
    if (!definition) {
        throw new Error(`Unsupported Foundation Phase subject code: ${subjectCode}`);
    }

    const maxOverrides = options.maxOverrides || {};
    const selectedComponentKeys = options.selectedComponentKeys || definition.components.map((component) => component.key);
    const selectedComponentSet = new Set(selectedComponentKeys);

    const componentResults = [];

    if (definition.mode === 'weighted') {
        let totalAchieved = 0;
        let totalPossible = 0;

        definition.components.forEach((component) => {
            if (!selectedComponentSet.has(component.key)) {
                return;
            }

            const rawScore = Number(components[component.key] ?? 0);
            const rawMax = Number(
                maxOverrides[component.key] ??
                component.sections.reduce((sum, section) => sum + section.maxScore, 0)
            );

            if (rawMax <= 0) {
                return;
            }

            const scaledMark = calculateComponentMark(rawScore, rawMax, component.reportScaleMax);
            const percentage = component.reportScaleMax > 0
                ? Math.round((scaledMark / component.reportScaleMax) * 100)
                : 0;
            const achieved = (percentage * component.weight) / 100;

            componentResults.push({
                key: component.key,
                label: component.label,
                rawScore,
                rawMax,
                scaledMark,
                scaledMax: component.reportScaleMax,
                percentage,
                weight: component.weight,
                achieved,
                possible: component.weight
            });

            totalAchieved += achieved;
            totalPossible += component.weight;
        });

        if (totalPossible <= 0) {
            return {
                subjectCode: definition.code,
                subjectName: definition.name,
                mode: definition.mode,
                percentage: null,
                ratingCode: null,
                totalAchieved: 0,
                totalPossible: 0,
                components: []
            };
        }

        const percentage = Math.round((totalAchieved / totalPossible) * 100);
        return {
            subjectCode: definition.code,
            subjectName: definition.name,
            mode: definition.mode,
            percentage,
            ratingCode: getNationalRatingCode(percentage),
            totalAchieved,
            totalPossible,
            components: componentResults
        };
    }

    let totalAchieved = 0;
    let totalPossible = 0;
    definition.components.forEach((component) => {
        if (!selectedComponentSet.has(component.key)) {
            return;
        }

        const rawScore = Number(components[component.key] ?? 0);
        const rawMax = Number(
            maxOverrides[component.key] ??
            component.sections.reduce((sum, section) => sum + section.maxScore, 0)
        );

        if (rawMax <= 0) {
            return;
        }

        const bounded = clamp(rawScore, 0, rawMax);
        const percentage = Math.round((bounded / rawMax) * 100);

        componentResults.push({
            key: component.key,
            label: component.label,
            rawScore: bounded,
            rawMax,
            percentage,
            achieved: bounded,
            possible: rawMax
        });

        totalAchieved += bounded;
        totalPossible += rawMax;
    });

    if (totalPossible <= 0) {
        return {
            subjectCode: definition.code,
            subjectName: definition.name,
            mode: definition.mode,
            percentage: null,
            ratingCode: null,
            totalAchieved: 0,
            totalPossible: 0,
            components: []
        };
    }

    const percentage = Math.round((totalAchieved / totalPossible) * 100);
    return {
        subjectCode: definition.code,
        subjectName: definition.name,
        mode: definition.mode,
        percentage,
        ratingCode: getNationalRatingCode(percentage),
        totalAchieved,
        totalPossible,
        components: componentResults
    };
}

export function buildSectionMarkLookup(sectionMarks) {
    const lookup = new Map();
    (sectionMarks || []).forEach((mark) => {
        lookup.set(`${mark.student_id}-${mark.section_id}`, mark);
    });
    return lookup;
}

export function calculateStandardSubjectResult(sections, markLookup, studentId) {
    let totalAchieved = 0;
    let totalPossible = 0;

    (sections || []).forEach((section) => {
        const markObj = markLookup.get(`${studentId}-${section.id}`);
        const mark = markObj && markObj.mark !== '' && markObj.mark != null
            ? parseFloat(markObj.mark)
            : null;
        const max = parseFloat(section.max_mark) || 100;

        if (mark !== null && !Number.isNaN(mark) && max > 0) {
            totalAchieved += mark;
            totalPossible += max;
        }
    });

    const percentage = totalPossible > 0 ? Math.round((totalAchieved / totalPossible) * 100) : null;
    return {
        isFoundation: false,
        percentage,
        ratingCode: percentage !== null ? getNationalRatingCode(percentage) : null,
        totalAchieved,
        totalPossible
    };
}

export function calculateFoundationSubjectResult(definition, sections, markLookup, studentId) {
    const componentScores = {};
    const componentMaxOverrides = {};
    const selectedComponentKeys = [];
    const groupedSections = new Map();
    const componentsByLabel = new Map(
        definition.components.map((component) => [component.label.toLowerCase(), component])
    );

    (sections || []).forEach((section) => {
        const groupLabel = normalizeText(section.assessment_group || section.section_name).toLowerCase();
        const component = componentsByLabel.get(groupLabel);
        if (!component) {
            return;
        }

        if (!groupedSections.has(component.key)) {
            groupedSections.set(component.key, { component, sections: [] });
        }
        groupedSections.get(component.key).sections.push(section);
    });

    groupedSections.forEach(({ component, sections: componentSections }, componentKey) => {
        let rawScore = 0;
        let rawMax = 0;
        let hasAnyMark = false;

        componentSections.forEach((section) => {
            const max = parseFloat(section.max_mark) || 0;
            if (max <= 0) {
                return;
            }

            rawMax += max;
            const markObj = markLookup.get(`${studentId}-${section.id}`);
            if (markObj && markObj.mark !== '' && markObj.mark != null && !Number.isNaN(parseFloat(markObj.mark))) {
                hasAnyMark = true;
                rawScore += parseFloat(markObj.mark);
            }
        });

        if (!hasAnyMark || rawMax <= 0) {
            return;
        }

        selectedComponentKeys.push(componentKey);
        componentScores[component.key] = rawScore;
        componentMaxOverrides[component.key] = rawMax;
    });

    const presentComponentKeys = selectedComponentKeys.filter((key) => componentMaxOverrides[key] > 0);
    const result = calculateSubjectMark(definition.code, componentScores, {
        maxOverrides: componentMaxOverrides,
        selectedComponentKeys: presentComponentKeys
    });

    return {
        isFoundation: true,
        ...result
    };
}

export function calculateSubjectResult({
    subjectName,
    subjectGrade,
    gradingMode,
    foundationTemplateCode,
    sections,
    markLookup,
    studentId
}) {
    const definition = getFoundationSubjectDefinition(subjectName, subjectGrade, gradingMode, foundationTemplateCode);
    if (!definition) {
        return calculateStandardSubjectResult(sections, markLookup, studentId);
    }
    return calculateFoundationSubjectResult(definition, sections, markLookup, studentId);
}

export function getFoundationGroupTarget(definition, groupLabel, groupRawMax = 0) {
    const component = definition.components.find(
        (entry) => entry.label.toLowerCase() === normalizeText(groupLabel).toLowerCase()
    );
    if (!component) {
        return groupRawMax;
    }
    return definition.mode === 'weighted'
        ? component.weight
        : component.sections.reduce((sum, section) => sum + section.maxScore, 0);
}

export function getFoundationOverallPossible(definition) {
    if (definition.mode === 'weighted') {
        return definition.components.reduce((sum, component) => sum + component.weight, 0);
    }
    return definition.components.reduce(
        (sum, component) => sum + component.sections.reduce((sectionSum, section) => sectionSum + section.maxScore, 0),
        0
    );
}

export function isFoundationManagedSubject(subjectName, grade, gradingMode, foundationTemplateCode) {
    return !!getFoundationSubjectDefinition(subjectName, grade, gradingMode, foundationTemplateCode);
}
